import type { ReactNode } from 'react';
import { InfoTooltip } from '../InfoTooltip';

/** Label row with an optional info tooltip. */
function Label({ label, info, htmlFor }: { label: string; info?: string; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} className="flex items-center text-xs font-medium text-slate-600 dark:text-slate-300">
      <span>{label}</span>
      {info && <InfoTooltip text={info} label={label} />}
    </label>
  );
}

const inputClass =
  'w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm tabular-nums focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500 dark:border-slate-600 dark:bg-slate-900';

let idSeq = 0;
const nextId = () => `f${++idSeq}`;

export function NumberField({
  label,
  info,
  value,
  onChange,
  min,
  max,
  step = 1,
  suffix,
}: {
  label: string;
  info?: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
}) {
  const id = nextId();
  return (
    <div className="flex flex-col gap-1">
      <Label label={label} info={info} htmlFor={id} />
      <div className="flex items-center gap-1">
        <input
          id={id}
          type="number"
          className={inputClass}
          value={Number.isFinite(value) ? value : ''}
          min={min}
          max={max}
          step={step}
          onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
        />
        {suffix && <span className="text-xs text-slate-500">{suffix}</span>}
      </div>
    </div>
  );
}

export function SliderField({
  label,
  info,
  value,
  onChange,
  min,
  max,
  step,
  format,
}: {
  label: string;
  info?: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step: number;
  format?: (v: number) => string;
}) {
  const id = nextId();
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <Label label={label} info={info} htmlFor={id} />
        <span className="text-xs font-semibold tabular-nums text-slate-700 dark:text-slate-200">
          {format ? format(value) : value}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <input
          id={id}
          type="range"
          className="h-1.5 flex-1 cursor-pointer appearance-none rounded bg-slate-200 accent-sky-600 dark:bg-slate-700"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
        />
        <input
          type="number"
          className={`${inputClass} w-24`}
          min={min}
          max={max}
          step={step}
          value={Number.isFinite(value) ? value : ''}
          onChange={(e) => onChange(e.target.value === '' ? min : Number(e.target.value))}
        />
      </div>
    </div>
  );
}

export function SelectField<T extends string | number>({
  label,
  info,
  value,
  options,
  onChange,
}: {
  label: string;
  info?: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  const id = nextId();
  const isNumber = typeof value === 'number';
  return (
    <div className="flex flex-col gap-1">
      <Label label={label} info={info} htmlFor={id} />
      <select
        id={id}
        className={inputClass}
        value={value}
        onChange={(e) => onChange((isNumber ? Number(e.target.value) : e.target.value) as T)}
      >
        {options.map((o) => (
          <option key={String(o.value)} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function CheckboxField({
  label,
  info,
  checked,
  onChange,
}: {
  label: string;
  info?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  const id = nextId();
  return (
    <div className="flex items-center gap-2">
      <input id={id} type="checkbox" className="h-4 w-4 accent-sky-600" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <Label label={label} info={info} htmlFor={id} />
    </div>
  );
}

export function Field({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-3">{children}</div>;
}
